/**
 * Calculator API Module
 * Handles all API calls for tile calculator functionality
 */

import client from './client';

/**
 * Create calculator configuration for a product (Admin only)
 */
export const createConfiguration = async (configData) => {
  const response = await client.post('/calculator/config', configData);
  return response.data;
};

/**
 * Update calculator configuration (Admin only)
 */
export const updateConfiguration = async (configId, updateData) => {
  const response = await client.put(`/calculator/config/${configId}`, updateData);
  return response.data;
};

/**
 * Delete calculator configuration (Admin only)
 */
export const deleteConfiguration = async (configId) => {
  const response = await client.delete(`/calculator/config/${configId}`);
  return response.data;
};

/**
 * Get configuration by product ID
 */
export const getConfigurationByProduct = async (productId) => {
  const response = await client.get(`/calculator/config/product/${productId}`);
  return response.data;
};

/**
 * Get all products with calculator configuration
 */
export const getConfiguredProducts = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append('search', filters.search);
  if (filters.brand_id) params.append('brand_id', filters.brand_id);
  if (filters.tile_length_mm) params.append('tile_length_mm', filters.tile_length_mm);
  if (filters.tile_width_mm) params.append('tile_width_mm', filters.tile_width_mm);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);

  const response = await client.get(`/calculator/products?${params.toString()}`);
  return response.data;
};

/**
 * Get available sizes for calculator (with calculator fields)
 */
export const getAvailableSizes = async () => {
  const response = await client.get('/calculator/sizes');
  return response.data;
};

/**
 * Perform tile calculation (legacy - single product)
 */
export const performCalculation = async (calculationData) => {
  const response = await client.post('/calculator/calculate', calculationData);
  return response.data;
};

/**
 * Perform multi-size tile calculation
 */
export const performMultiSizeCalculation = async (calculationData) => {
  const response = await client.post('/calculator/calculate-multi', calculationData);
  return response.data;
};

/**
 * Get calculation history
 */
export const getCalculationHistory = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.product_id) params.append('product_id', filters.product_id);
  if (filters.customer_id) params.append('customer_id', filters.customer_id);
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.is_converted !== undefined) params.append('is_converted', filters.is_converted);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.addressed_to) params.append('addressed_to', filters.addressed_to);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);

  const response = await client.get(`/calculator/history?${params.toString()}`);
  return response.data;
};

/**
 * Get single calculation by ID
 */
export const getCalculationById = async (calculationId) => {
  const response = await client.get(`/calculator/${calculationId}`);
  return response.data;
};

/**
 * Convert calculation to sale
 */
export const convertToSale = async (calculationId, saleData) => {
  const response = await client.post(`/calculator/${calculationId}/convert-to-sale`, saleData);
  return response.data;
};

/**
 * Update calculation address
 */
export const updateCalculationAddress = async (calculationId, addressedPerson) => {
  const response = await client.patch(`/calculator/${calculationId}/address`, { addressed_person: addressedPerson });
  return response.data;
};

/**
 * Get calculator statistics
 */
export const getCalculatorStatistics = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);

  const response = await client.get(`/calculator/statistics?${params.toString()}`);
  return response.data;
};

// =============== QUOTATION API ===============

/**
 * Create quotation
 */
export const createQuotation = async (quotationData) => {
  const response = await client.post('/calculator/quotation', quotationData);
  return response.data;
};

/**
 * Get quotation by ID
 */
export const getQuotation = async (quotationId) => {
  const response = await client.get(`/calculator/quotation/${quotationId}`);
  return response.data;
};

/**
 * Get quotations list
 */
export const getQuotations = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.customer_id) params.append('customer_id', filters.customer_id);
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);

  const response = await client.get(`/calculator/quotations?${params.toString()}`);
  return response.data;
};

/**
 * Update quotation
 */
export const updateQuotation = async (quotationId, updateData) => {
  const response = await client.put(`/calculator/quotation/${quotationId}`, updateData);
  return response.data;
};

/**
 * Delete quotation
 */
export const deleteQuotation = async (quotationId) => {
  const response = await client.delete(`/calculator/quotation/${quotationId}`);
  return response.data;
};

/**
 * Get quotation preview (text format)
 */
export const getQuotationPreview = async (quotationId) => {
  const response = await client.get(`/calculator/quotation/${quotationId}/preview`);
  return response.data;
};

/**
 * Send quotation via WhatsApp
 */
export const sendQuotationWhatsApp = async (quotationId, phone) => {
  const response = await client.post(`/calculator/quotation/${quotationId}/send-whatsapp`, { phone });
  return response.data;
};

/**
 * Send quotation via Email
 */
export const sendQuotationEmail = async (quotationId, email) => {
  const response = await client.post(`/calculator/quotation/${quotationId}/send-email`, { email });
  return response.data;
};

/**
 * Download quotation as file
 */
export const downloadQuotation = async (quotationId) => {
  const response = await client.get(`/calculator/quotation/${quotationId}/download`, {
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `quotation-${quotationId}.txt`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default {
  createConfiguration,
  updateConfiguration,
  deleteConfiguration,
  getConfigurationByProduct,
  getConfiguredProducts,
  getAvailableSizes,
  performCalculation,
  performMultiSizeCalculation,
  getCalculationHistory,
  getCalculationById,
  convertToSale,
  getCalculatorStatistics,
  // Quotation
  createQuotation,
  getQuotation,
  getQuotations,
  updateQuotation,
  deleteQuotation,
  getQuotationPreview,
  sendQuotationWhatsApp,
  sendQuotationEmail,
  downloadQuotation
};

